import React, { useState, useRef, useEffect, ReactNode } from 'react';
import {
  AlignLeft,
  Layers,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  MessageSquare,
  Brain,
} from 'lucide-react';
import { Button } from '../../../ui/Button';
import { cn } from '../../../../lib/utils';
// import { SummaryTab } from './SummaryTab';
import { RepurposeTab } from '../../universal/output-tabs/RepurposeTab';
import { ChatTab } from '../../universal/output-tabs/ChatTab';
import { MindmapTab } from '../../universal/output-tabs/MindmapTab';
import { CustomOutputProps, SummarizerConfig } from '@/types';
import { useMediaQuery } from 'usehooks-ts';
import { SummaryTab } from '../../universal/output-tabs/SummaryTab';
import { useSummarizer } from '../../../../core/store/useSummarizer';
type OutputTabType = 'summary' | 'repurpose' | 'chat' | 'mindmap';

interface TextSummarizerOutputProps extends Omit<CustomOutputProps, 'config'> {
  config?: SummarizerConfig;
}

const TextSummarizerOutput: React.FC<TextSummarizerOutputProps> = ({ summary, config }) => {
  const [activeTab, setActiveTab] = useState<OutputTabType>('summary');
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const isMobile = useMediaQuery('(max-width: 500px)');
  const { error } = useSummarizer();

  // Auto-switch to Summary tab when new summary arrives
  useEffect(() => {
    if (summary) {
      setActiveTab('summary');
    }
  }, [summary?.id]);

  // Define all possible tabs with their default properties
  const allTabs: {
    id: OutputTabType;
    label: string;
    icon: ReactNode;
    enabled: boolean;
  }[] = [
    {
      id: 'summary' as OutputTabType,
      label: config?.outputs?.summaryTabLabel || 'Summary',
      icon: <AlignLeft className="w-4 h-4" />,
      enabled: config?.outputs?.summary !== false,
    },
    {
      id: 'repurpose' as OutputTabType,
      label: 'Repurpose',
      icon: <Layers className="w-4 h-4" />,
      enabled: config?.outputs?.repurpose === true,
    },
    {
      id: 'chat' as OutputTabType,
      label: 'AI Chat',
      icon: <MessageSquare className="w-4 h-4" />,
      enabled: config?.outputs?.chat !== false,
    },
    {
      id: 'mindmap' as OutputTabType,
      label: 'Mind Map',
      icon: <Brain className="w-4 h-4" />,
      enabled: config?.outputs?.mindmap !== false,
    },
  ];

  // Apply custom ordering if specified in config, otherwise use default order
  const customOrder = config?.outputs?.tabsOrder;
  const availableTabs = customOrder && customOrder.length > 0
    ? // Use custom order: map each ID to its tab definition, filter enabled
      customOrder
        .map(tabId => allTabs.find(tab => tab.id === tabId))
        .filter((tab): tab is NonNullable<typeof tab> => !!tab && tab.enabled)
        .concat(
          // Add any tabs not in custom order but still enabled
          allTabs.filter(tab => tab.enabled && !customOrder.includes(tab.id))
        )
    : // Use default order: just filter enabled tabs
      allTabs.filter(tab => tab.enabled);

  // Scroll detection effect
  useEffect(() => {
    const checkOverflow = () => {
      if (tabsContainerRef.current) {
        const { scrollWidth, clientWidth } = tabsContainerRef.current;
        setShowScrollButtons(scrollWidth > clientWidth);
      }
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    if (tabsContainerRef.current) {
      resizeObserver.observe(tabsContainerRef.current);
    }

    return () => {
      if (tabsContainerRef.current) {
        resizeObserver.unobserve(tabsContainerRef.current);
      }
    };
  }, [availableTabs]);

  // Scroll function
  const scroll = (scrollOffset: number) => {
    if (tabsContainerRef.current) {
      tabsContainerRef.current.scrollLeft += scrollOffset;
    }
  };

  return (
    <>
      {/* Tab Navigation - Mobile scrollable */}
      <div className="sticky top-0 w-full bg-white z-10">
        <div className="relative flex-shrink-0">
          {showScrollButtons && (
            <Button
              variant="outline"
              size="icon"
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-7 w-7 sm:!h-8 sm:!w-8 rounded-full"
              onClick={() => scroll(-100)}
            >
              <ChevronLeft className="w-3 h-3 sm:!w-4 sm:!h-4" />
            </Button>
          )}
          <div
            ref={tabsContainerRef}
            className="flex items-center gap-1 sm:!gap-2 p-3 sm:!p-4 border-b overflow-x-auto no-scrollbar scrollbar-hide sticky top-0 w-full bg-white z-10"
          >
            {availableTabs.map((tab) => {
              return (
                <Button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  variant="ghost"
                  disabled={!summary}
                  className={cn(
                    '!bg-[#EFEFEF] w-full md:w-auto hover:bg-gray-300 !text-gray-900 font-normal min-h-[36px] sm:!h-10 px-2 sm:!px-3 text-xs sm:!text-sm transition-colors whitespace-nowrap',
                    activeTab === tab.id &&
                      '!bg-sky-500 hover:!bg-sky-500 !text-white hover:!text-white'
                  )}
                >
                  <span>{tab.icon}</span>
                  <span
                    className={cn(
                      activeTab !== tab.id && isMobile && 'hidden sm:!flex',
                      'whitespace-nowrap'
                    )}
                  >
                    {tab.label}
                  </span>
                </Button>
              );
            })}
          </div>
          {showScrollButtons && (
            <Button
              variant="outline"
              size="icon"
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-7 w-7 sm:!h-8 sm:!w-8 rounded-full"
              onClick={() => scroll(100)}
            >
              <ChevronRight className="w-3 h-3 sm:!w-4 sm:!h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Tab Content */}
      {summary ? (
        <>
          {activeTab === 'summary' && <SummaryTab summary={summary} />}
          {activeTab === 'repurpose' && <RepurposeTab />}
          {activeTab === 'chat' && <ChatTab summary={summary} />}
          {activeTab === 'mindmap' && <MindmapTab summary={summary} />}
        </>
      ) : error ? (
        <div className="p-6 flex flex-col items-center justify-center min-h-[200px]">
          <div className="w-full max-w-md">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 mb-1">Error occurred</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
            <p className="text-sm text-gray-500 text-center mt-4">
              Please try again or check your input
            </p>
          </div>
        </div>
      ) : (
        <div className="p-6 text-center text-gray-500 min-h-[200px] flex items-center justify-center">
          <div>
            <p className="text-base font-medium text-gray-700 mb-2">No summary yet</p>
            <p className="text-sm">Enter text and click Summarize to see results</p>
          </div>
        </div>
      )}
    </>
  );
};

export default TextSummarizerOutput;
